/**
 * User Profile Epic Error Handling
 * Centralized error codes, messages, and utilities
 */

export { UserProfileErrorCode, STATUS_TO_ERROR_CODE } from './errorCodes';
export {
  USER_PROFILE_ERROR_MESSAGES,
  getErrorMessage,
  extractErrorMessage,
  type ApiErrorResponse,
} from './errorMessages';

/**
 * Error handler utility for user profile API calls
 */
import { UserProfileErrorCode, STATUS_TO_ERROR_CODE } from './errorCodes';
import { getErrorMessage, extractErrorMessage } from './errorMessages';

export interface ErrorHandlerResult {
  errorCode: UserProfileErrorCode;
  errorMessage: string;
}

/**
 * Handle API error and return standardized error information
 */
export const handleUserProfileError = (
  status?: number,
  errorData?: unknown,
  isNetworkError?: boolean
): ErrorHandlerResult => {
  // Handle network errors
  if (isNetworkError) {
    return {
      errorCode: UserProfileErrorCode.NETWORK_ERROR,
      errorMessage: getErrorMessage(UserProfileErrorCode.NETWORK_ERROR),
    };
  }

  // Handle HTTP status codes
  if (status) {
    const errorCode =
      STATUS_TO_ERROR_CODE[status] || UserProfileErrorCode.UNKNOWN_ERROR;

    // Special handling for 400 errors
    if (status === 400) {
      const message = extractErrorMessage(
        errorData,
        UserProfileErrorCode.VALIDATION_ERROR
      );

      // Check for specific error messages
      const rawMessageLower =
        typeof errorData === 'object' &&
        errorData !== null &&
        'message' in errorData &&
        typeof (errorData as { message?: unknown }).message === 'string'
          ? ((errorData as { message: string }).message ?? '').toLowerCase()
          : message.toLowerCase();

      if (rawMessageLower.includes('user not found')) {
        return {
          errorCode: UserProfileErrorCode.USER_NOT_FOUND,
          errorMessage: getErrorMessage(UserProfileErrorCode.USER_NOT_FOUND),
        };
      }

      if (
        rawMessageLower.includes('current password') &&
        rawMessageLower.includes('incorrect')
      ) {
        return {
          errorCode: UserProfileErrorCode.CURRENT_PASSWORD_INCORRECT,
          errorMessage: getErrorMessage(
            UserProfileErrorCode.CURRENT_PASSWORD_INCORRECT
          ),
        };
      }

      if (
        rawMessageLower.includes('password') &&
        rawMessageLower.includes('requirements')
      ) {
        return {
          errorCode: UserProfileErrorCode.PASSWORD_REQUIREMENTS_NOT_MET,
          errorMessage: getErrorMessage(
            UserProfileErrorCode.PASSWORD_REQUIREMENTS_NOT_MET
          ),
        };
      }

      if (
        rawMessageLower.includes('reuse') ||
        rawMessageLower.includes('last 5 passwords')
      ) {
        return {
          errorCode: UserProfileErrorCode.PASSWORD_REUSE_ERROR,
          errorMessage: getErrorMessage(
            UserProfileErrorCode.PASSWORD_REUSE_ERROR
          ),
        };
      }

      // Check if it's account deactivated
      if (rawMessageLower.includes('deactivated')) {
        return {
          errorCode: UserProfileErrorCode.ACCOUNT_DEACTIVATED,
          errorMessage: getErrorMessage(
            UserProfileErrorCode.ACCOUNT_DEACTIVATED
          ),
        };
      }

      // Check if it's validation error with details
      if (
        errorData &&
        typeof errorData === 'object' &&
        'details' in errorData
      ) {
        return {
          errorCode: UserProfileErrorCode.VALIDATION_ERROR,
          errorMessage: extractErrorMessage(
            errorData,
            UserProfileErrorCode.VALIDATION_ERROR
          ),
        };
      }

      return {
        errorCode: UserProfileErrorCode.VALIDATION_ERROR,
        errorMessage: message,
      };
    }

    // Special handling for 401 errors
    if (status === 401) {
      return {
        errorCode: UserProfileErrorCode.UNAUTHORIZED,
        errorMessage: extractErrorMessage(
          errorData,
          UserProfileErrorCode.UNAUTHORIZED
        ),
      };
    }

    // Special handling for 403 errors
    if (status === 403) {
      return {
        errorCode: UserProfileErrorCode.FORBIDDEN,
        errorMessage: extractErrorMessage(
          errorData,
          UserProfileErrorCode.FORBIDDEN
        ),
      };
    }

    // Special handling for 500 errors
    if (status === 500) {
      return {
        errorCode: UserProfileErrorCode.SERVER_ERROR,
        errorMessage: extractErrorMessage(
          errorData,
          UserProfileErrorCode.SERVER_ERROR
        ),
      };
    }

    // For other status codes, use mapped error code
    return {
      errorCode,
      errorMessage: extractErrorMessage(errorData, errorCode),
    };
  }

  // Unknown error
  return {
    errorCode: UserProfileErrorCode.UNKNOWN_ERROR,
    errorMessage: extractErrorMessage(
      errorData,
      UserProfileErrorCode.UNKNOWN_ERROR
    ),
  };
};
